/* eslint-disable max-len */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox as AntCheckbox,
  Col,
  Row,
  Typography,
  Image,
  Divider,
  Space,
} from 'antd';
import {
  DatePicker,
  Form,
  FormItem,
  Input,
  Checkbox,
} from 'formik-antd';
import { Formik } from 'formik';
import { hasExpirationDays, isExpirationDateDisabled, textColor } from '@helpers/index';
import { CloseOutlined } from '@ant-design/icons';
import Modal from '@components/molecules/modal';
import DetailText from '@components/atoms/detail-text-label';
import TableStatus from '@components/molecules/table-status';
import BasicButtonBG from '@components/atoms/basic-button-bg';
import DocumentAlertPreviewIframe from '../../molecules/document-alert-preview-iframe';
import {
  DocumentAlertReviewModalProps,
  StencilChecklistsParams,
} from './types';

const buildUpdatedChecklist = (
  currentChecklist: number[] | null | undefined,
  itemId: number,
  isChecked: boolean,
): number[] => {
  const checklist = currentChecklist ?? [];
  return isChecked
    ? [...checklist, itemId]
    : checklist.filter((id) => id !== itemId);
};

const DocumentAlertReviewModal = ({
  handleDownloadFile,
  reviewModalInitialValues,
  reviewModalFormValidation,
  indefiniteTerm,
  handleIndefiniteTerm,
  setIsReviewModalVisible,
  isReviewModalVisible,
  showExpirationDate,
  employeeName,
  employeeStatus,
  downloadParams,
  documentName,
  rut,
  plate,
  code,
  category,
  expirable,
  docStencilCheckList,
  resourceUrl,
  contractorName,
  contractorRut,
  period,
  expirationDays,
  onSubmit,
  rutHeader,
  contractorRutHeader,
  loadComment,
}: DocumentAlertReviewModalProps) => {
  const { t } = useTranslation();
  const { Text, Paragraph } = Typography;
  const { TextArea } = Input;
  const [clickedAction, setClickedAction] = useState<'approve' | 'reject' | null>(null);
  const hasConfiguredExpirationDays = hasExpirationDays(expirationDays);

  const handleOkDetailModal = () => setIsReviewModalVisible(true);
  const handleCancelDetailModal = () => setIsReviewModalVisible(false);
  const onHandleDownloadFile = () => handleDownloadFile(downloadParams);
  const shouldShowContractorDetails = (): boolean => !rut && !plate;

  const downloadButton = (
    <Button
      type="link"
      icon={(
        <Image
          wrapperClassName="align-middle"
          height={30}
          width={30}
          src="/images/pdf.svg"
          preview={false}
        />
      )}
      onClick={onHandleDownloadFile}
    >
      <Text className={`${textColor.lightBlue} ml-2 underline`}>
        {t('alert.downloadDocument')}
      </Text>
    </Button>
  );
  return (
    <Row>
      <Formik
        enableReinitialize
        onSubmit={onSubmit}
        initialValues={reviewModalInitialValues}
        validationSchema={reviewModalFormValidation}
      >
        {(formikProps) => {
          const stencilItems = Array.isArray(docStencilCheckList) ? docStencilCheckList : [];
          const negativeItems = stencilItems.filter((i) => Boolean(i.negative));
          const positiveItems = stencilItems.filter((i) => !i.negative);
          const negativeIds = negativeItems.map((i) => i.id);
          const positiveIds = positiveItems.map((i) => i.id);
          const selectedChecklist = Array.isArray(formikProps.values.checklist)
            ? formikProps.values.checklist
            : [];
          const negativeSelectedCount = selectedChecklist.filter((id) => (
            negativeIds.includes(id)
          )).length;

          return (
            <Modal
              title={<Text className="text-lg text-blue-900">{t('alert.reviewFile')}</Text>}
              closeIcon={<CloseOutlined className="text-2xl" />}
              visible={isReviewModalVisible}
              onOk={handleOkDetailModal}
              onCancel={handleCancelDetailModal}
              okText="Edit"
              displayButtons={false}
              footer={(
                <Row>
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="flex flex-row justify-start"
                  >
                    {downloadButton}
                  </Col>
                  <Col
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    className="flex flex-row justify-end"
                  >
                    <Space size="small">
                      <BasicButtonBG
                        size="middle"
                        type="default"
                        buttonColorType="lightRed"
                        key="reject"
                        loading={formikProps.isSubmitting && clickedAction === 'reject'}
                        onClick={(e) => {
                          e.preventDefault();
                          setClickedAction('reject');
                          formikProps.setFieldValue('approved', false);
                          formikProps.setFieldValue('isRejection', true);
                          formikProps.validateForm().then(() => {
                            formikProps.submitForm();
                          });
                        }}
                        text={(
                          <Text className={`${textColor.red} py-0.5 text-sm xs:text-xs sm:text-xs md:text-xs lg:text-xs xl:text-xs 2xl:text-xs`}>
                            {t('alert.refuse')}
                          </Text>
                      )}
                      />
                      <BasicButtonBG
                        size="middle"
                        type="primary"
                        buttonColorType="blue"
                        key="approve"
                        loading={formikProps.isSubmitting && clickedAction === 'approve'}
                        onClick={(e) => {
                          e.preventDefault();
                          setClickedAction('approve');
                          formikProps.setFieldValue('approved', true);
                          formikProps.setFieldValue('isRejection', false);
                          formikProps.validateForm().then(() => {
                            formikProps.submitForm();
                          });
                        }}
                        text={(
                          <Text className={`${textColor.white} py-0.5 text-sm xs:text-xs sm:text-xs md:text-xs lg:text-xs xl:text-xs 2xl:text-xs`}>
                            {t('alert.approve')}
                          </Text>
                      )}
                      />
                    </Space>
                  </Col>
                </Row>
            )}
              width={1200}
            >
              <Form>
                <Row>
                  <Col xs={15} sm={15} md={15} lg={15} xl={15}>
                    <DocumentAlertPreviewIframe
                      resourceUrl={resourceUrl}
                      width="98%"
                      height="650"
                      title="Ksec PDF Read"
                    />
                  </Col>
                  <Col xs={9} sm={9} md={9} lg={9} xl={9}>
                    <Row>
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <DetailText
                          header={t('alert.document')}
                          content={documentName}
                          textColor={textColor.gray}
                        />
                        <Divider plain />
                      </Col>
                      {contractorRut && (
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <DetailText
                          header={t('alert.contractorRut')}
                          content={contractorRut}
                          textColor={textColor.gray}
                        />
                        <Divider plain />
                      </Col>
                      )}
                      {plate && (
                      <>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                          <DetailText
                            header={t('alert.plates')}
                            content={plate}
                            textColor={textColor.gray}
                          />
                          <Divider plain />
                        </Col>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                          <DetailText
                            header={t('vehicle.externalId')}
                            content={code}
                            textColor={textColor.gray}
                          />
                          <Divider plain />
                        </Col>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                          <DetailText
                            header={t('vehicle.category')}
                            content={category}
                            textColor={textColor.gray}
                          />
                          <Divider plain />
                        </Col>
                      </>
                      )}
                      {rut && (
                      <>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                          <DetailText
                            header={t('alert.employeeName')}
                            content={employeeName}
                            textColor={textColor.gray}
                          />
                          <TableStatus text={employeeStatus} />
                          <Divider plain />
                        </Col>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                          <DetailText
                            header={rutHeader || t('alert.rut')}
                            content={rut}
                            textColor={textColor.gray}
                          />
                          <Divider plain />
                        </Col>
                      </>
                      )}
                      { shouldShowContractorDetails() && (
                      <>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                          <DetailText
                            header={t('alert.contractorCompanyName')}
                            content={contractorName}
                            textColor={textColor.gray}
                          />
                          <Divider plain />
                        </Col>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                          <DetailText
                            header={t('alert.contractorRut')}
                            content={contractorRut}
                            textColor={textColor.gray}
                          />
                        </Col>
                      </>
                      )}
                      {!expirable && (
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <FormItem name="indefiniteTerm">
                          <Checkbox name="indefiniteTerm" onChange={handleIndefiniteTerm} checked={indefiniteTerm}>
                            {t('documentation.indefiniteTerm')}
                          </Checkbox>
                        </FormItem>
                        <Divider plain />
                      </Col>
                      )}
                      {showExpirationDate && (
                      <Col>
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={24} md={24} lg={24} xl={24} className="my-4">
                            <Paragraph className="text-gray-500">{t('alert.expirationDate')}</Paragraph>
                            {hasConfiguredExpirationDays && (
                              <Paragraph className="text-gray-500">
                                {t('documentation.configuredExpirationDays')}
                                {' '}
                                <Text className="text-orange-500">
                                  {expirationDays}
                                  {' '}
                                  {t('documentation.expirationDaysUnit')}
                                </Text>
                              </Paragraph>
                            )}
                            <FormItem name="expiresAt">
                              <DatePicker
                                disabled={indefiniteTerm}
                                name="expiresAt"
                                className="w-full"
                                format="DD/MM/YYYY"
                                placeholder="01/01/2021"
                                disabledDate={(current) => isExpirationDateDisabled(
                                  current,
                                  expirationDays,
                                )}
                              />
                            </FormItem>
                          </Col>
                        </Row>
                        <Divider plain />
                      </Col>
                      )}
                      {!showExpirationDate && (
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <DetailText
                          header={t('alert.period')}
                          content={period}
                          textColor={textColor.gray}
                        />
                        <Divider plain />
                      </Col>
                      )}
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <DetailText
                          header={t('documentation.commentOfLoad')}
                          content={(loadComment ?? '').trim() || '\u00A0'}
                          textColor={textColor.gray}
                        />
                        <Divider plain />
                      </Col>
                      <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                        <Paragraph className="text-gray-500">{t('documentation.commentOfReview')}</Paragraph>
                        <FormItem name="revisionComment">
                          <TextArea
                            name="revisionComment"
                            placeholder={t('alert.comment')}
                            allowClear
                            size="middle"
                            rows={2}
                          />
                        </FormItem>
                      </Col>
                      {stencilItems.length > 0 && (
                      <>
                        {negativeItems.length > 0 && (
                          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Divider plain className="my-2" />
                            <FormItem name="checklist" style={{ marginBottom: 0 }} className="ksec-checklist-formitem">
                              <div className="py-1">
                                <Text className="font-semibold text-sm block">
                                  {t('alert.missingItemChecklist')}
                                </Text>
                                <Text className="text-xs text-gray-500 block mt-1">
                                  {t('alert.checkboxesInstruction')}
                                </Text>
                                {negativeSelectedCount > 0 && (
                                  <Text className="text-red-500 text-xs block mt-1">
                                    {t('alert.requirementsMissing', { count: negativeSelectedCount })}
                                  </Text>
                                )}
                                <Space direction="vertical" size={2} className="mt-2 mb-0">
                                  {negativeItems.map((item: StencilChecklistsParams) => {
                                    const isMissing = selectedChecklist.includes(item.id);
                                    return (
                                      <AntCheckbox
                                        key={item.id}
                                        checked={isMissing}
                                        onChange={(e) => {
                                          const posSel = selectedChecklist.filter((id) => (
                                            positiveIds.includes(id)
                                          ));
                                          const currentNeg = selectedChecklist.filter((id) => (
                                            negativeIds.includes(id)
                                          ));
                                          const nextNeg = buildUpdatedChecklist(
                                            currentNeg,
                                            item.id,
                                            e.target.checked,
                                          );
                                          formikProps.setFieldValue('checklist', [...posSel, ...nextNeg]);
                                        }}
                                      >
                                        <span className={isMissing ? 'text-red-500' : ''}>
                                          {isMissing && <CloseOutlined className="mr-1" />}
                                          {item.text}
                                        </span>
                                      </AntCheckbox>
                                    );
                                  })}
                                </Space>
                              </div>
                            </FormItem>
                          </Col>
                        )}
                        {positiveItems.length > 0 && (
                          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Divider plain />
                            <Paragraph className="text-gray-500">{t('alert.checklist')}</Paragraph>
                            <AntCheckbox.Group
                              options={positiveItems.map((c) => ({ label: c.text, value: c.id }))}
                              value={
                                selectedChecklist.filter((id) => positiveIds.includes(id))
                              }
                              onChange={(vals) => {
                                const negSel = selectedChecklist.filter((id) => (
                                  negativeIds.includes(id)
                                ));
                                formikProps.setFieldValue('checklist', [...negSel, ...(vals as number[])]);
                              }}
                            />
                          </Col>
                        )}
                      </>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Form>
            </Modal>
          );
        }}
      </Formik>
    </Row>
  );
};

export default DocumentAlertReviewModal;
